<template>
  <div class="downloads">
    <Hero
      class="hero"
      heading="Sales Material"
      :heroBackgroundImage="'sales-material.jpg'"
      :heroOverlay="'lightOverlay'"
    />
    <div class="action-bar-container">
      <ListTag
        :topic="'sales_material'"
        :editMode="editMode"
        :permissions="tagPermissions"
        :enableAllTag="true"
        :selectCallback="onTagsUpdate"
        :updateCallback="generateTags"
        class="list-tag"
      ></ListTag>
      <EditToggle
        v-if="hasEditTogglePermissions"
        :callback="editToggle"
        class="edit-toggle"
      />
    </div>
    <PageContainer>
      <div class="download-links">
        <div v-for="(topicData, topicKey) in mappedFiles" :key="topicKey" class="topic-container">
          <div :id="'anchor-' + topicKey" class="header-container">
            <div class="header-topic-container">
              <h2 class="title">{{ topicData.displayName }}</h2>
            </div>
            <p v-if="topicData.description" v-html="topicData.description"></p>
          </div>
          <Modal
            title="Upload File"
            :show="showModal[topicKey]"
            @close="showModal[topicKey] = false"
          >
            <FileUploaderMultiple
              :files="[
                { accepts: '.pdf,.pptx', required: true },
              ]"
              :topicKey="topicKey"
              :securityLevel="'information'"
              :unionHash="generateUnionHash()"
              :successCallback="filesUpdate"
            >
            </FileUploaderMultiple>
          </Modal>

          <div class="filter-contianer">
            <select name="pages_mode" v-model="filter.pagesMode">
              <option value="ALL">All pages</option>
              <option value="MORETHAN">More than</option>
              <option value="LESSTHAN">Less than</option>
            </select>
            <select
              name="num_pages"
              v-model="filter.numPages"
              :disabled="filter.pagesMode === 'ALL'"
            >
              <option value="NA"></option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
            <button
              class="filter-button pdf-filter"
              :class="{
                active: filter.documentType.includes('PDF')
              }"
              v-html="'PDF'"
              @click="toggleFilterButton('PDF')"
            />
            <button
              class="filter-button ppt-filter"
              :class="{
                active: filter.documentType.includes('PPT')
              }"
              v-html="'PPT'"
              @click="toggleFilterButton('PPT')"
            />
            <div class="filter-container-right">
              <button
                class="show-categories-button"
                :class="{
                  active: showFileTags
                }"
                v-html="'Show categories'"
                @click="showFileTags = !showFileTags"
              />
              <div
                v-if="hasCreatePermissions"
                @click="showModal[topicKey] = true"
                class="icon">
              </div>
            </div>
          </div>
          <div v-for="file in topicData.files" :key="file.Key">
            <DownloadButtonBlob
              class="btn"
              :file="file"
              :topicKey="topicKey"
              :securityLevel="'information'"
              :accepts="'.pdf,.pptx'"
              :hasEditPermissions="hasEditPermissions"
              :hasReplacePermissions="hasReplacePermissions"
              :hasDeletePermissions="hasDeletePermissions"
              :deleteCallback="deleteFile"
              :replaceCallback="filesUpdate"
              :editCallback="filesUpdate"
            >
              {{ file.alias }}
            </DownloadButtonBlob>
            <div class="tag-container" v-if="showFileTags">
              <div class="tag" v-html="getFileTagsDisplay(file)"></div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  </div>
</template>
<script>
import Hero from '@/components/hero.vue';
import PageContainer from '@/components/page-container.vue';
import DownloadButtonBlob from '@/components/download-button-blob.vue';
import FileUploaderMultiple from '@/components/file-uploader-multiple.vue';
import Modal from '@/components/modal.vue';
import EditToggle from '@/components/edit-toggle.vue';
import ListTag from '@/components/tag-list.vue';

import dynamoDB from '@/aws/aws-dynamoDB';

import generateRandomHash from '@/utils/generateRandomHash';
import intersect from '@/utils/arrayIntersec';

import MixinPermissions from '@/mixins/mixin-permissions';
import MixinFileListing from '@/mixins/mixin-file-listing';

export default {
  name: 'ListFiles',
  components: {
    Hero,
    PageContainer,
    DownloadButtonBlob,
    FileUploaderMultiple,
    Modal,
    EditToggle,
    ListTag,
  },
  mixins: [
    MixinPermissions,
    MixinFileListing(
      {
        sales_material: { displayName: '', files: [] },
      },
      'information',
      'sales_material',
    ),
  ],
  data() {
    return {
      showModal: {},
      mappedFiles: {},
      tags: [],
      selectedTags: [],
      filter: {
        pagesMode: 'ALL',
        numPages: 'NA',
        documentType: ['PDF', 'PPT'],
      },
      showFileTags: false,
      editMode: false,
    };
  },
  computed: {
    tagPermissions() {
      return {
        create: this.hasTagCreatePermissions,
        edit: this.hasTagEditPermissions,
        delete: this.hasTagDeletePermissions,
      };
    },
  },
  // Which files are shown at any times depends on:
  // A. which files exist
  // B. which tags exits
  // C. which tags are selected
  // D. which filter is applied
  //
  // A. this data should remain static for any page load. It is only
  // necessary to watch for changes for the initial creation of the component.
  // (files are created in mixin-file-listing.js)
  //
  // B. this data can change any time a user creates, updates, or deletes a tag.
  // The reason UI should be reactive to this data is because the user can
  // change tag display name without page load.
  //
  // C. this data can change any time a user toggles a tag.
  //
  // D. User selects number of pages and document type to filter by.
  watch: {
    files: {
      deep: true,
      handler() {
        this.mapFiles();
      },
    },
    tags: {
      deep: true,
      handler() {
        this.mapFiles();
      },
    },
    selectedTags: {
      deep: true,
      handler() {
        this.mapFiles();
      },
    },
    filter: {
      deep: true,
      handler() {
        if (this.filter.pagesMode === 'ALL') {
          this.filter.numPages = 'NA';
        }

        this.mapFiles();
      },
    },
  },
  async created() {
    // initialize showModal for each topicKey
    Object.keys(this.files).forEach((topicKey) => {
      this.$set(this.showModal, topicKey, false);
    });

    this.generateTags();
  },
  methods: {
    fileMapper(files) {
      const mappedFiles = {};
      Object.keys(files).forEach((topicKey) => {
        mappedFiles[topicKey] = {
          ...files[topicKey],
          files: files[topicKey].files
            // filter out any files that don't match the selected tags the user has selected
            .filter((file) => {
              if (this.selectedTags.length === 0) {
                return true;
              }
              if (this.selectedTags.includes('ALL')) {
                return true;
              }

              const tagIds = file.metadata && file.metadata.tags ? file.metadata.tags : [];

              return intersect(tagIds, this.selectedTags);
            })
            // filter by number of page selection
            .filter((file) => {
              if (this.filter.pagesMode === 'ALL' || this.filter.numPages === 'NA') {
                return true;
              }

              const numPages = file.metadata && file.metadata.numPages ? file.metadata.numPages : 0;
              switch (this.filter.pagesMode) {
                case 'LESSTHAN':
                  return numPages < this.filter.numPages;
                case 'MORETHAN':
                  return numPages > this.filter.numPages;
                default:
                  return true;
              }
            })
            // filter by document type
            .filter((file) => {
              const fileType = file.metadata && file.metadata.type ? file.metadata.type : '';
              let simpleFileType = null;
              if (fileType === 'application/pdf') {
                simpleFileType = 'PDF';
              } else if (fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
                simpleFileType = 'PPT';
              }
              return this.filter.documentType.includes(simpleFileType);
            })
            .sort((a, b) => {
              // sort by timestamp if no tags are selected
              if (this.selectedTags.length === 0 || this.selectedTags.includes('ALL')) {
                const aTimestamp = a.metadata && a.metadata.uploadedTimestamp
                  ? a.metadata.uploadedTimestamp : 0;
                const bTimestamp = b.metadata && b.metadata.uploadedTimestamp
                  ? b.metadata.uploadedTimestamp : 0;

                return bTimestamp - aTimestamp;
              }

              // when tags are selected sort by:
              // 1. file names (alias)
              // 2. number of pages
              const nameComparison = a.alias.localeCompare(b.alias);
              if (nameComparison !== 0) {
                return nameComparison;
              }

              const aNumPages = a.metadata && a.metadata.numPages ? a.metadata.numPages : 0;
              const bNumPages = b.metadata && b.metadata.numPages ? b.metadata.numPages : 0;

              return aNumPages - bNumPages;
            })
            // Populate the file metadata with the tag names. Files are only stored
            // with tag ids, and since tags can be updated without page load, we
            // need to keep tag names fresh.
            .map(file => ({
              ...file,
              metadata: {
                ...file.metadata,
                tags: file.metadata && file.metadata.tags
                  ? this.tags
                    .filter(tag => file.metadata.tags.includes(tag.id))
                  : [],
              },
            })),
        };
      });

      return mappedFiles;
    },
    mapFiles() {
      this.$set(this, 'mappedFiles', this.fileMapper(this.files));
    },
    deleteFile(key) {
      // Update files the reactive files object and thereby the UI
      const topicKey = Object.keys(this.files)
        .find(_topicKey => key.indexOf(_topicKey) !== -1);
      this.files[topicKey].files = this.files[topicKey].files
        .filter(file => file.key !== key);
    },
    /**
     * Covers both upload and replace scenarios
     */
    uploadFile(fileObject) {
      // remove old file
      this.files[fileObject.topicKey].files = this.files[fileObject.topicKey].files
        .filter(file => file.key !== fileObject.key);
      // add new file
      this.files[fileObject.topicKey].files.push(fileObject);
      // close modal
      this.showModal[fileObject.topicKey] = false;
      this.sortFiles();
    },
    editToggle(onOrOff) {
      this.editMode = onOrOff;
    },
    filesUpdate(topicKey) {
      this.showModal[topicKey] = false;
      // function can be found found in mixin-file-listing.js
      this.generateFilelist();
    },
    generateUnionHash() {
      return generateRandomHash(12);
    },
    onTagsUpdate(selectedTags) {
      this.selectedTags = selectedTags;
    },
    getFileTags(file) {
      return file.metadata && file.metadata.tags ? file.metadata.tags : [];
    },
    getFileTagsDisplay(file) {
      return this.getFileTags(file).map(tag => tag.name).join(', ');
    },
    toggleFilterButton(fileType) {
      if (this.filter.documentType.includes(fileType)) {
        this.filter.documentType = this.filter.documentType
          .filter(_fileType => _fileType !== fileType);
      } else {
        this.filter.documentType.push(fileType);
      }
    },
    async generateTags() {
      this.tags = await dynamoDB.getDBTagsByTopic('sales_material');
    },
  },
};
</script>

<style lang="stylus" scoped>
  @require '../style/colors'
  .comingSoon
    display: block
    margin: 8rem 0
    text-align: center

    p
      max-width: 70rem
      margin: 0 auto
      padding: 0 2rem
      font-size: 4rem
      color: $primary-color

      @media only screen and (max-width: 600px)
        font-size: 3rem
        padding: 3rem

  .hero
    margin-bottom: 30px

  .download-links
    display: flex
    flex-direction: column
    padding: 0 60px
    max-width: 100rem
    justify-content: center
    margin: 0 auto 30px auto
  .btn
    margin-top: 20px

  .topic-container
    margin: 10px 0 0 0

  .header-container
    display: flex
    flex-direction: column
    align-items: center
    width: 100%

    .header-topic-container
      display: flex
      flex-direction: row
      justify-content: center;
      align-items: center

  .icon
    width: 22px
    height: 22px
    background: url('../assets/addButton.svg') no-repeat
    background-size: contain

    &:hover
      cursor: pointer

  .action-bar-container
    display: grid
    grid-template-columns: 1fr 4fr 1fr
    justify-items: center
    align-items: center
    margin-bottom: 2rem
    gap: 4rem

    .list-tag
      max-width: 100rem
      grid-column: 2

    .edit-toggle
      grid-column: 3
      justify-self: end
      align-self: flex-start

  .tag-container
    display: flex
    flex-flow: row nowrap
    justify-content: flex-start

    .tag
      font-size: 1.5rem
      color: $black30

  .filter-contianer
    display: flex
    flex-flow: row nowrap
    justify-content: flex-start
    align-items: center
    gap: 1.5rem

    .filter-container-right
     flex-grow: 100 // assume remaining space
     display: flex
     flex-flow: row nowrap
     justify-content: flex-end
     align-items: center
     column-gap: 15px

    select
      font-size: 1.5rem
      background-color: $secondary-color
      color: $primary-color
      border-color: $secondary-color
      border-radius: 2rem
      font-weight: 900
      padding: .7rem .7rem .7rem 1.2rem
      text-align: left
      border-right: 1rem solid transparent

      &:disabled
        color: $black60
        background-color: $black30
        border-color: $black30

  .show-categories-button
  .filter-button
    padding: .7rem 2rem
    border-radius: 2rem
    font-weight: 900
    cursor: pointer
    transition: all 0.3s ease-in-out

    &.pdf-filter
      border: 3px solid $primary-color
      background-color: $primary-color
      color: $white

      &:not(.active)
        background-color: $white
        color: $primary-color

      &:hover
        transform: scale(1.1)


    &.ppt-filter
      border: 3px solid $black
      background-color: $black
      color: $white

      &:not(.active)
        background-color: $white
        color: $black

      &:hover
        transform: scale(1.1)

  .show-categories-button
    background-color: #DDD
    border: none

    &.active
      color: $black60

    &:not(.active)
      background-color: lighten($black30, 70%)
      color: lighten($black60, 70%)

</style>
